import { Edge, uniqueId, createRefTypeInfoCache } from 'graph/edge';
import {
  formatDateShort,
  formatDateTimeShort,
  parseDate,
  startOfDay,
} from '@ardoq/date-time';
import { fieldOps } from 'models/utils/fieldOps';
import {
  APIComponentAttributes,
  APIFieldType,
  APIReferenceAttributes,
  APITagAttributes,
  APIWorkspaceAttributes,
  GroupingRule,
} from '@ardoq/api-types';
import { toByIdDictionary } from '@ardoq/common-helpers';
import { FieldValue } from 'graph/FieldValue';
import { ComponentTypeValue } from 'graph/ComponentTypeValue';
import * as encodingUtils from '@ardoq/html';
import { fieldUtils } from '@ardoq/scope-data';
import { createComponentNode, createNode } from './createNode';
import type { NodeCollection } from 'graph/NodeCollection';
import type { GraphCollection } from 'graph/GraphCollection';
import type { Tag } from 'aqTypes';
import { groupingRuleOperations, usersOperations } from '@ardoq/core';
import type {
  AggregatedGraphBuilderNode,
  Node as GraphNode,
  Edge as EdgeType,
  Relation,
} from './types';
import { referenceOperations } from '@ardoq/reference-operations';
import { createComponentTypeInfoCache } from './componentTypeInfo';
import { GraphModelData } from './graphModelData';
import { filterCache } from 'collections/filterCache';
import { getDisplayLabel } from 'modelInterface/util';

function buildAggregatedGraph(
  rootNodeCollection: NodeCollection,
  edgeCollection: GraphCollection<EdgeType>,
  groupingRules: GroupingRule[],
  graphModelData: GraphModelData
) {
  const compMap = new Map<string, AggregatedGraphBuilderNode | undefined>();
  const edges: Edge[] = [];
  const rootNodes: GraphNode[] = [];
  const getComponentTypeInfo = createComponentTypeInfoCache(graphModelData);
  const formatUserFieldFn = (val: unknown): string | null =>
    usersOperations.formatUserField(graphModelData.orgUsersById, val);

  // clean up old nodes/edges
  rootNodeCollection.clear({ ignoreNodeUpdates: true });
  edgeCollection.clear({ ignoreNodeUpdates: true });

  function aggNode(
    comp:
      | APIComponentAttributes
      | ComponentTypeValue
      | FieldValue
      | Tag
      | APITagAttributes
      | APIWorkspaceAttributes,
    id: string
  ): AggregatedGraphBuilderNode {
    let node = compMap.get(id);
    if (!node) {
      node = {
        id,
        component: comp,
        aggregated: [],
        aggregators: [],
        incoming: [],
        outgoing: [],
        rootNodes: [],
        nodes: [],
      };
      compMap.set(id, node);
    }
    return node;
  }

  function aggregate(
    aggregator: AggregatedGraphBuilderNode,
    aggregatee: AggregatedGraphBuilderNode,
    ref?: APIReferenceAttributes
  ): void {
    aggregator.aggregated.push({
      agg: aggregatee,
      ref: ref,
    });
    aggregatee.aggregators.push({
      agg: aggregator,
      ref: ref,
    });
  }

  const allComponents = graphModelData.allComponents;
  const componentsById = toByIdDictionary(allComponents);

  const componentTypesValues = new Map();
  allComponents.forEach(function (compAttributes) {
    const workspaceId = compAttributes.rootWorkspace;
    const parentId = compAttributes.parent;
    const parentAttributes = parentId
      ? (componentsById[parentId] ?? null)
      : null;

    const aggregateParentChild =
      parentAttributes &&
      (groupingRuleOperations.hasGroupByParentAll(groupingRules) ||
        groupingRuleOperations.hasGroupByParent(
          groupingRules,
          parentAttributes
        ) ||
        groupingRuleOperations.hasGroupByChild(groupingRules, compAttributes));
    if (aggregateParentChild) {
      aggregate(
        aggNode(parentAttributes, parentAttributes._id),
        aggNode(compAttributes, compAttributes._id)
      );
    }

    if (groupingRuleOperations.hasGroupByComponentType(groupingRules)) {
      const myType = getComponentTypeInfo(compAttributes).componentType;
      if (!myType) {
        return;
      }
      const typeId = compAttributes.typeId;
      let ct = componentTypesValues.get(typeId);
      if (!ct) {
        ct = new ComponentTypeValue({ componentType: myType });
        componentTypesValues.set(typeId, ct);
      }
      aggregate(
        aggNode(ct, typeId),
        aggNode(compAttributes, compAttributes._id)
      );
    }

    if (groupingRuleOperations.hasGroupByWorkspace(groupingRules)) {
      const aggComp = aggNode(compAttributes, compAttributes._id);
      // don't aggregate already aggregated nodes
      if (aggComp.aggregators.length === 0) {
        const workspaceData = graphModelData.workspacesById[workspaceId];
        if (workspaceData) {
          aggregate(aggNode(workspaceData, workspaceId), aggComp);
        }
      }
    }
  });

  graphModelData.allTags.forEach(tag => {
    if (groupingRuleOperations.hasGroupByTag(groupingRules, { id: tag._id })) {
      (tag.components ?? []).forEach(compId => {
        const compAttrs = componentsById[compId];
        if (!compAttrs) return;
        const tagNode = aggNode(tag, tag._id);
        const compNode = aggNode(compAttrs, compId);
        aggregate(tagNode, compNode);
      });
    }
  });

  graphModelData.allFields.forEach(function (field) {
    if (groupingRuleOperations.hasGroupByField(groupingRules, field)) {
      const fieldValueCache = new Map();
      /*
       * Ensure correct order of field values by inserting them based on the order
       * specified in the fields allowed values array.
       */
      const isListFieldType =
        field.type === APIFieldType.LIST ||
        field.type === APIFieldType.SELECT_MULTIPLE_LIST;
      if (isListFieldType) {
        fieldOps.getAllowedValues(field).forEach((fieldValue: string) => {
          const value = String(fieldValue).toLowerCase();
          if (value) compMap.set(value, undefined);
        });
      }
      const isGroupByDateWithoutTime = field.type === APIFieldType.DATE_TIME;

      allComponents.forEach(function (compAttrs) {
        const { typeId, model } = compAttrs;
        const fieldAppliesToComponent = Boolean(
          typeId &&
          field.model === model &&
          fieldOps.hasComponentType(field, typeId)
        );
        if (!fieldAppliesToComponent) {
          return;
        }

        const fieldName = field.name;
        let valueKey = getComponentFieldValue(compAttrs, fieldName);
        if (isGroupByDateWithoutTime && valueKey) {
          valueKey = startOfDay(parseDate(valueKey)).toString();
        }
        const mapKey = valueKey.toLowerCase ? valueKey.toLowerCase() : valueKey;
        let value = valueKey;
        let fv = fieldValueCache.get(mapKey);
        if (!fv) {
          if (field.type === APIFieldType.DATE_TIME) {
            const locale = graphModelData.locale;
            const fieldValue = getComponentFieldValue(compAttrs, fieldName);

            value = isGroupByDateWithoutTime
              ? formatDateShort(fieldValue, locale)
              : formatDateTimeShort(fieldValue, locale);
          } else {
            const formattedValue = fieldUtils.fieldAttributesToLabel({
              fieldAttributes: field,
              fieldType: field.type,
              value,
              formatUserFieldFn,
              i18nLocale: graphModelData.i18nLocale,
            });
            value = encodingUtils.unescapeHTML(formattedValue ?? '');
          }
          fv = new FieldValue({
            field,
            value,
            cid: mapKey,
          });
          fieldValueCache.set(mapKey, fv);
        }

        const fieldNode = aggNode(fv, fv.cid);
        const compNode = aggNode(compAttrs, compAttrs._id);
        aggregate(fieldNode, compNode);
      });

      /* Remove unused field values from compMap to ensure aggregation works */
      if (isListFieldType) {
        const unusedFieldValues = fieldOps
          .getAllowedValues(field)
          .map(String)
          .map(fieldValue => fieldValue.toLowerCase())
          .filter(fieldValue => !fieldValueCache.has(fieldValue));

        unusedFieldValues.forEach(fieldValue => compMap.delete(fieldValue));
      }
    }
  });

  graphModelData.allReferences
    .filter(reference => graphModelData.includedReferenceIds.has(reference._id))
    .forEach(ref => {
      const sourceComp = componentsById[ref.source];
      const targetComp = componentsById[ref.target];
      if (!sourceComp || !targetComp) return;
      const source = aggNode(sourceComp, ref.source),
        target = aggNode(targetComp, ref.target);

      if (
        groupingRuleOperations.hasGroupByOutgoingReference(groupingRules, ref)
      ) {
        aggregate(source, target, ref);
      } else if (
        groupingRuleOperations.hasGroupByIncomingReference(groupingRules, ref)
      ) {
        aggregate(target, source, ref);
      } else {
        source.outgoing.push(ref);
        target.incoming.push(ref);
      }
    });

  function isCircularParentDependency(
    componentId: string,
    parentNode: GraphNode
  ): boolean {
    if (componentId === parentNode.id) {
      return true;
    }
    if (parentNode.parent) {
      return isCircularParentDependency(componentId, parentNode.parent);
    }
    return false;
  }

  function createGraphNode(agg: AggregatedGraphBuilderNode): GraphNode {
    const component = componentsById[agg.id];
    return component === agg.component
      ? createComponentNode({ dataModel: component, getComponentTypeInfo })
      : createNode({ dataModel: agg.component });
  }

  const MAX_DEPTH = 10;

  function createAggregatedNodes(
    rootAgg: AggregatedGraphBuilderNode,
    parentNode: GraphNode,
    relation: Relation,
    depth: number
  ): void {
    if (depth > MAX_DEPTH) {
      return;
    }
    if (isCircularParentDependency(relation.agg.id, parentNode)) {
      return;
    }

    const aggregateeNode = createGraphNode(relation.agg);

    const aggregateeIsIncluded = aggregateeNode.isComponent()
      ? graphModelData.includedComponentIds.has(relation.agg.id)
      : aggregateeNode.isIncludedInContextByFilter();
    if (!aggregateeIsIncluded) {
      aggregateeNode.destroy();
      return;
    }
    parentNode.children.add(aggregateeNode);

    relation.agg.rootNodes.push(rootAgg.rootNodes[0]);
    relation.agg.nodes.push(aggregateeNode);

    relation.agg.aggregated.forEach(function (nestedAggregatee: Relation) {
      createAggregatedNodes(
        rootAgg,
        aggregateeNode,
        nestedAggregatee,
        depth + 1
      );
    });
  }

  compMap.forEach(rootAgg => {
    if (!rootAgg || rootAgg.aggregators.length !== 0) {
      return;
    }
    const rootNode = createGraphNode(rootAgg);
    const rootIsIncluded = rootNode.isComponent()
      ? graphModelData.includedComponentIds.has(rootAgg.id)
      : rootNode.isIncludedInContextByFilter();
    if (!rootIsIncluded) {
      rootNode.destroy();
      return;
    }
    rootAgg.rootNodes.push(rootNode);
    rootAgg.nodes.push(rootNode);
    rootNodes.push(rootNode);
    rootAgg.aggregated.forEach(function (aggregatee) {
      createAggregatedNodes(rootAgg, rootNode, aggregatee, 1);
    });
  });

  const getRefTypeInfo = createRefTypeInfoCache(graphModelData);

  const { value, includeTime } = graphModelData.referenceLabelFilter;
  const labelField = graphModelData.labelField;

  compMap.forEach(agg => {
    if (!agg) {
      return;
    }
    agg.nodes.forEach(target => {
      const aggregatedTarget = target.getRootParent();

      agg.incoming.forEach(reference => {
        const aggNode = compMap.get(reference.source);
        const sources = (aggNode && aggNode.nodes) || [];
        const { referenceType, urlFields } = getRefTypeInfo(reference);

        sources.forEach(source => {
          edges.push(
            new Edge({
              uniqueId: uniqueId(),
              reference,
              referenceType,
              filterColor: filterCache.getColor(reference._id),
              displayLabel:
                getDisplayLabel(
                  reference,
                  labelField,
                  referenceType,
                  value,
                  !includeTime
                ) || '',
              hasUrlField: referenceOperations.hasURLFields(
                reference,
                urlFields
              ),
              sourceNode: source,
              targetNode: target,
              aggregatedSourceNode: source.getRootParent(),
              aggregatedTargetNode: aggregatedTarget,
            })
          );
        });
      });
    });
  });

  rootNodes.forEach(node => rootNodeCollection.add(node));
  edges.forEach(edge => edgeCollection.add(edge));

  return compMap;
}

function getComponentFieldValue(
  component: APIComponentAttributes,
  fieldName: string
): any {
  const value = component[fieldName] || '';
  if (Array.isArray(value)) {
    return value.sort().join(',');
  }
  return value;
}

export { buildAggregatedGraph };

export type { AggregatedGraphBuilderNode } from './types';
